import React, { useState, useEffect, useRef } from 'react';
import './ConversationModeSimulator.css';

const ConversationModeSimulator = ({ isActive, onVideoShow }) => {
  const [transcriptText, setTranscriptText] = useState('');
  const [isListening, setIsListening] = useState(false);
  const [showVideo, setShowVideo] = useState(false);
  const videoRef = useRef(null);
  const fullText = "Can this drug cause GI issues?";

  useEffect(() => {
    if (!isActive) {
      setTranscriptText('');
      setIsListening(false);
      setShowVideo(false);
      if (onVideoShow) onVideoShow(false);
      // Pause video if it's playing
      if (videoRef.current) {
        videoRef.current.pause();
      }
      return;
    }

    // Start listening animation after a brief delay
    const startDelay = setTimeout(() => {
      setIsListening(true);
    }, 500);

    // Simulate speech-to-text appearing word by word (slower)
    const words = fullText.split(' ');
    let currentIndex = 0;
    let builtText = '';
    let videoTimeout;

    const intervalId = setInterval(() => {
      if (currentIndex < words.length) {
        builtText = builtText + (builtText ? ' ' : '') + words[currentIndex];
        setTranscriptText(builtText);
        currentIndex++;
      } else {
        // Stop listening animation when done
        setTimeout(() => {
          setIsListening(false);
          
          // After 2 seconds, show the video
          videoTimeout = setTimeout(() => {
            setShowVideo(true);
          }, 2000);
        }, 300);
        clearInterval(intervalId);
      }
    }, 467); // Each word appears every 467ms (1.5x faster, mimics real speech)

    return () => {
      clearTimeout(startDelay);
      clearTimeout(videoTimeout);
      clearInterval(intervalId);
    };
  }, [isActive, onVideoShow]);

  // Notify parent when video shows
  useEffect(() => {
    if (onVideoShow) {
      onVideoShow(showVideo);
    }
  }, [showVideo, onVideoShow]);

  return (
    <div className="conversation-mode-container">
      {/* Voice Listener Visualization */}
      <div className="voice-listener-wrapper">
        <div className={`voice-pulse-ring ${isListening ? 'listening' : ''}`}></div>
        <div className={`voice-pulse-ring-2 ${isListening ? 'listening' : ''}`}></div>
        <div className={`voice-pulse-ring-3 ${isListening ? 'listening' : ''}`}></div>
        <div className={`voice-listener-circle ${isListening ? 'active' : ''}`}>
          <svg width="60" height="60" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path 
              d="M12 14C13.66 14 15 12.66 15 11V5C15 3.34 13.66 2 12 2C10.34 2 9 3.34 9 5V11C9 12.66 10.34 14 12 14Z" 
              fill="currentColor"
            />
            <path 
              d="M17 11C17 13.76 14.76 16 12 16C9.24 16 7 13.76 7 11H5C5 14.53 7.61 17.43 11 17.92V21H13V17.92C16.39 17.43 19 14.53 19 11H17Z" 
              fill="currentColor"
            />
          </svg>
        </div>
      </div>

      {/* Waveform visualization */}
      <div className="waveform-container">
        {[...Array(20)].map((_, i) => (
          <div 
            key={i} 
            className={`waveform-bar ${isListening ? 'active' : ''}`}
            style={{ animationDelay: `${i * 0.05}s` }}
          ></div>
        ))}
      </div>

      {/* Speech-to-Text Transcript */}
      <div className="transcript-container">
        <div className="transcript-label">
          {isListening ? (
            <>
              <span className="listening-indicator"></span>
              Listening...
            </>
          ) : transcriptText ? (
            'Transcript'
          ) : (
            'Tap microphone to ask'
          )}
        </div>
        <div className="transcript-text">
          {transcriptText}
          {isListening && transcriptText && <span className="cursor-blink">|</span>}
        </div>
      </div>

      {/* AI Response Indicator */}
      {transcriptText === fullText && !isListening && !showVideo && (
        <div className="ai-response-indicator">
          <div className="thinking-dots">
            <span></span>
            <span></span>
            <span></span>
          </div>
          <span className="response-text">Creating visual explainer...</span>
        </div>
      )}

      {/* Video Response */}
      {showVideo && (
        <div className="video-response-container">
          <video
            ref={videoRef}
            src="/snorkle_vis_thumbnails/lecanemab_GI.mp4"
            autoPlay
            loop
            playsInline
            className="response-video"
          />
        </div>
      )}
    </div>
  );
};

export default ConversationModeSimulator;
